import { useState } from 'react';
import styled from 'styled-components';
import { FaFileAlt, FaExternalLinkAlt, FaChevronDown, FaChevronUp, FaCopy, FaCheck } from 'react-icons/fa';

const SectionWrapper = styled.section`
  padding: 6rem 1.5rem;
  background-color: var(--bg-primary);
  width: 100%;
  position: relative;

  @media (max-width: 480px) {
    padding: 3.5rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
`;

const SectionTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.875rem;
  color: var(--accent-cyan);
  background: rgba(6, 182, 212, 0.1);
  border: 1px solid rgba(6, 182, 212, 0.2);
  padding: 0.35rem 0.85rem;
  border-radius: 9999px;
  display: inline-block;
  margin-bottom: 0.85rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.35rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.5px;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.85rem;
  }

  @media (max-width: 480px) {
    font-size: 1.55rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  color: var(--text-muted);
  max-width: 600px;
  margin: 0 auto;
`;

const PublicationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(340px, 100%), 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PublicationCard = styled.article`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-left: 4px solid var(--accent-cyan);
  border-radius: 16px;
  padding: 2rem;

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
  box-shadow: var(--shadow-sm);
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  position: relative;
  backdrop-filter: blur(8px);
  min-width: 0;

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-md), var(--shadow-glow);
    border-color: var(--border-hover);
    border-left: 4px solid var(--accent-purple);
  }
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const Badge = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.65rem;
  border-radius: 6px;
  background: ${props => (props.$primary ? 'rgba(56, 189, 248, 0.15)' : 'var(--bg-tertiary)')};
  color: ${props => (props.$primary ? 'var(--accent-cyan)' : 'var(--text-secondary)')};
  border: 1px solid ${props => (props.$primary ? 'rgba(56, 189, 248, 0.3)' : 'var(--border-subtle)')};
`;

const PaperTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--text-primary);
  line-height: 1.5;
  font-weight: 600;
  margin: 0;
  overflow-wrap: break-word;

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const Authors = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-muted);
  line-height: 1.5;

  strong {
    color: var(--text-secondary);
  }
`;

const ConferenceBox = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 0.9rem 1.1rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  strong {
    color: var(--accent-cyan);
    font-weight: 600;
  }
`;

const AbstractContainer = styled.div`
  border-top: 1px solid var(--border-subtle);
  padding-top: 1rem;
`;

const AbstractHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const AbstractTitle = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToggleBtn = styled.button`
  background: transparent;
  border: none;
  color: var(--accent-cyan);
  font-size: 0.825rem;
  font-family: var(--font-mono);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem;

  &:hover {
    color: var(--text-primary);
  }
`;

const AbstractText = styled.p`
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.65;
  ${props =>
    !props.$isExpanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: auto;
  padding-top: 0.5rem;
`;

const PaperLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.75rem 1.4rem;
  background: var(--btn-primary-bg, #2563eb);
  color: var(--btn-primary-text, #ffffff);
  text-decoration: none;
  border: 1px solid var(--btn-primary-border, rgba(255, 255, 255, 0.15));
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.25s ease;
  box-shadow: var(--btn-primary-shadow, 0 1px 2px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(37, 99, 235, 0.25));

  &:hover {
    background: var(--btn-primary-hover, #1d4ed8);
    transform: translateY(-2px);
    box-shadow: var(--btn-primary-shadow-hover, 0 2px 6px rgba(0, 0, 0, 0.12), 0 6px 16px rgba(37, 99, 235, 0.35));
    color: #ffffff;
  }

  &:active {
    background: var(--btn-primary-active, #1e40af);
    transform: translateY(0);
  }
`;

const DoiBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: var(--bg-secondary);
  border: 1px dashed var(--border-subtle);
  border-radius: 8px;
  padding: 0.45rem 0.75rem;
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-dim);
  flex-wrap: wrap;
`;

const DoiText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: var(--accent-cyan);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-family: var(--font-mono);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--border-subtle);
    color: var(--text-primary);
  }
`;

function PublicationCardItem({ publication }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Conference badge helper
  const confBadge = publication.conference.includes('ICRITO')
    ? 'ICRITO 2025'
    : publication.conference.includes('AIEI')
    ? 'AIEI 2026'
    : 'Conference';

  const handleCopyDoi = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(publication.doi);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <PublicationCard>
      <BadgeRow>
        <Badge $primary>IEEE Indexed</Badge>
        <Badge>{confBadge}</Badge>
        <Badge style={{ marginLeft: 'auto' }}>{publication.date}</Badge>
      </BadgeRow>

      <PaperTitle>{publication.title}</PaperTitle>

      <Authors>
        <strong>Authors:</strong> {publication.authors.join(', ')}
      </Authors>

      <ConferenceInfoBox publication={publication} />

      <AbstractContainer>
        <AbstractHeader onClick={() => setIsExpanded(!isExpanded)}>
          <AbstractTitle>
            <FaFileAlt style={{ color: 'var(--accent-cyan)' }} /> Abstract
          </AbstractTitle>
          <ToggleBtn type="button">
            {isExpanded ? (
              <>
                Less <FaChevronUp />
              </>
            ) : (
              <>
                Read more <FaChevronDown />
              </>
            )}
          </ToggleBtn>
        </AbstractHeader>
        <AbstractText $isExpanded={isExpanded}>{publication.abstract}</AbstractText>
      </AbstractContainer>

      <ActionRow>
        <PaperLink href={publication.paperLink} target="_blank" rel="noopener noreferrer">
          <FaExternalLinkAlt /> Read Paper on IEEE Xplore
        </PaperLink>

        <DoiBox>
          <DoiText title={`DOI: ${publication.doi}`}>
            DOI: {publication.doi}
          </DoiText>
          <CopyButton onClick={handleCopyDoi} title="Copy DOI">
            {copied ? (
              <>
                <FaCheck color="#10b981" /> Copied
              </>
            ) : (
              <>
                <FaCopy /> Copy
              </>
            )}
          </CopyButton>
        </DoiBox>
      </ActionRow>
    </PublicationCard>
  );
}

function ConferenceInfoBox({ publication }) {
  return (
    <ConferenceBox>
      <div>
        <strong>Conference:</strong> {publication.conference}
      </div>
      <div>
        <strong>Location:</strong> {publication.location}
      </div>
    </ConferenceBox>
  );
}

export default function Publications({ publications }) {
  return (
    <SectionWrapper id="research">
      <Container>
        <SectionHeader>
          <SectionTag>04. // research</SectionTag>
          <SectionTitle>Research Publications</SectionTitle>
          <SectionSubtitle>
            Published at IEEE-indexed conferences focusing on privacy-preserving Federated Learning in medical healthcare.
          </SectionSubtitle>
        </SectionHeader>

        <PublicationsGrid>
          {publications.map((pub) => (
            <PublicationCardItem key={pub.id} publication={pub} />
          ))}
        </PublicationsGrid>
      </Container>
    </SectionWrapper>
  );
}
